import { observer } from 'mobx-react-lite';

import { Spinner } from '@fluentui/react/lib/Spinner';
import {
    FileProviderType,
    FileProviderViewState,
    lazyAddFileProvider,
    lazyLoadProviders,
    lazyRemoveFileProvider,
    ProviderLoadStateOption,
} from 'owa-fileprovider-store';
import { getAddableFileProviders } from 'owa-fileprovider-store/lib/selectors/getAddableFileProviders';
import getAddedProviders from '../../utils/getAddedProviders';
import { getProviderLoadState } from 'owa-fileprovider-store/lib/selectors/getProviderLoadState';
import { isProviderBeingRemoved } from 'owa-fileprovider-store/lib/selectors/isProviderBeingRemoved';
import getProviderBrandName from 'owa-fileprovider-store/lib/utils/getProviderBrandName';
import { LightningId } from 'owa-lightning-v2';
import loc from 'owa-localize';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import * as React from 'react';
import { AddStorageAccountCallout } from './AddStorageAccountCallout';
import {
    storageAccounts_addAccountHeader,
    storageAccounts_otherAccounts,
} from './FileProviderAccountsView.locstring.json';
import FileProvidersToAddListView, { FileProviderToAddInfo } from './FileProvidersToAddListView';
import HeaderContainer from './HeaderContainer';
import StorageAccountsListView from './StorageAccountsListView';

const styles = require('./FileProviderAccountsView.scss');

export default observer(function FileProviderAccountsView(props: React.HTMLProps<HTMLDivElement>) {
    React.useEffect(() => {
        // Initialize the addFileProvider to avoid popup blocker to trigger
        lazyAddFileProvider.import().then(addFileProvider => {
            addFileProviderAction.current = addFileProvider;
        });
        initializeStorageAccountsOption();
    }, []);
    const addFileProviderAction = React.useRef<(providerType: FileProviderType) => void>(null);
    const calloutTarget = React.useRef<HTMLDivElement>();
    const setTarget = () => {
        return calloutTarget.current;
    };
    const getStorageAccountToAdd = (accountsToAdd: FileProviderType[]): FileProviderToAddInfo[] => {
        return accountsToAdd.map(account => {
            return {
                providerType: account,
                providerName: getProviderBrandName(account),
                onClick: getAddStorageAccountFunc(account),
            };
        });
    };
    const getAddStorageAccountFunc = (providerType: FileProviderType) => {
        return () => {
            if (addFileProviderAction.current) {
                addFileProviderAction.current(providerType);
            } else {
                lazyAddFileProvider.import().then(addFileProviderAction => {
                    addFileProviderAction(providerType);
                });
            }
        };
    };
    const addedAccounts = getAddedProviders();
    const additionalAccounts = getAdditionalStorageAccounts(addedAccounts);
    const providersToAddInfo = getStorageAccountToAdd(getAddableFileProviders());
    return getProviderLoadState() === ProviderLoadStateOption.Loaded ? (
        <div className={styles.container}>
            <div>
                {providersToAddInfo.length !== 0 && (
                    <div>
                        <HeaderContainer
                            className={styles.fileProvidersToAddContainer}
                            ref={calloutTarget}
                            header={loc(storageAccounts_addAccountHeader)}>
                            <FileProvidersToAddListView providersToAddInfo={providersToAddInfo} />
                        </HeaderContainer>
                        <AddStorageAccountCallout
                            lid={LightningId.AddStorageAccountCallout} // lightning id for callout
                            when={activateCallout}
                            target={setTarget}
                        />
                    </div>
                )}

                {additionalAccounts && additionalAccounts.length !== 0 && (
                    <StorageAccountsListView
                        header={loc(storageAccounts_otherAccounts)}
                        storageAccounts={additionalAccounts}
                        onRemoveHandler={removeAccount}
                    />
                )}
            </div>
        </div>
    ) : (
        <Spinner />
    );
});

function activateCallout(lightup) {
    setTimeout(lightup, 500);
}

async function initializeStorageAccountsOption() {
    (await lazyLoadProviders.import())();
}

function getAdditionalStorageAccounts(providers: FileProviderViewState[]): FileProviderViewState[] {
    return providers.filter(
        p =>
            !p.isPrimaryProvider &&
            !p.associatedWithPrimaryProvider &&
            p.type !== AttachmentDataProviderType.Mailbox &&
            p.type !== AttachmentDataProviderType.MailMessage
    );
}

function removeAccount(providerType: FileProviderType) {
    if (!isProviderBeingRemoved(providerType)) {
        lazyRemoveFileProvider.import().then(removeFileProviderAction => {
            removeFileProviderAction(providerType);
        });
    }
}
